import React from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { isForgotPasswordLogin } from '@/features/auth/constants/authRoutes';
import { Button } from '@/components/ui';

interface AuthHeaderProps {
  className?: string;
  children?: React.ReactNode;
  title?: string;
  subtitle?: string;
}

/**
 * Header for authentication pages (User Login, Activate, Forgot Password, Reset Password):
 * chrome surface, "Back to home" link, page title and subtitle (title hidden below md).
 */
const AuthHeader: React.FC<AuthHeaderProps> = ({
  className = '',
  children,
  title: propTitle,
  subtitle: propSubtitle
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const isForgotLogin = location.pathname === '/login' && isForgotPasswordLogin(searchParams);

  // Determine title and subtitle based on route
  const getTitle = () => {
    if (propTitle) return propTitle;
    if (isForgotLogin) return t('authHeader.forgotPassword');
    if (location.pathname === '/login') return t('authHeader.userLogin');
    if (location.pathname === '/activate') return t('authHeader.accountActivation');
    if (location.pathname === '/forgot-password') return t('authHeader.forgotPassword');
    if (location.pathname === '/reset-password') return t('authHeader.resetPassword');
    return t('authHeader.userLogin');
  };

  const getSubtitle = () => {
    if (propSubtitle) return propSubtitle;
    if (isForgotLogin) return t('authHeader.forgotSubtitle');
    if (location.pathname === '/login') return t('authHeader.verifyIdentitySubtitle');
    if (location.pathname === '/activate') return t('authHeader.activationSubtitle');
    if (location.pathname === '/forgot-password') return t('authHeader.forgotSubtitle');
    if (location.pathname === '/reset-password') return t('authHeader.resetSubtitle');
    return t('authHeader.verifyIdentitySubtitle');
  };

  const title = getTitle();
  const subtitle = getSubtitle();

  const handleBackClick = () => {
    navigate('/');
  };

  if (children) {
    return <header className={`w-full ${className}`}>{children}</header>;
  }

  const isLoginPage = location.pathname === '/login' || location.pathname === '/forgot-password';
  const backLabel = t('authHeader.backToHome');

  return (
    <header
      className={`
        relative flex h-full min-h-0 w-full items-center
        px-3 py-1.5 @bp-sm:px-4 @bp-sm:py-2 @bp-md:px-5 @bp-lg:px-8
        border-b border-edge-subtle bg-chrome backdrop-blur-md
        ${className}
      `}
    >
      {isLoginPage ? (
        <div className="relative flex w-full items-center justify-center">
          <div className="absolute left-0 top-1/2 -translate-y-1/2">
            <Button variant="ghost" size="sm" icon={<ArrowLeft />} onClick={handleBackClick} aria-label={backLabel}>
              <span className="hidden @bp-sm:inline">{backLabel}</span>
            </Button>
          </div>

          <div className="pointer-events-none hidden flex-col items-center justify-center gap-0.5 @bp-md:flex">
            <h1 className="m-0 text-center font-display text-title-lg font-bold text-primary">{title}</h1>
            <p className="m-0 max-w-50 truncate text-center font-ui text-small text-secondary @bp-sm:max-w-none">
              {subtitle}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex w-full flex-row items-center justify-between gap-2">
          <Button
            variant="ghost"
            size="sm"
            icon={<ArrowLeft />}
            onClick={handleBackClick}
            className="shrink-0"
          >
            {backLabel}
          </Button>

          <div className="hidden min-w-0 flex-1 flex-col items-center justify-center gap-0.5 @bp-md:flex">
            <h1 className="m-0 w-full truncate text-center font-display text-title-lg font-bold text-primary">
              {title}
            </h1>
            <p className="m-0 w-full truncate text-center font-ui text-small text-secondary">{subtitle}</p>
          </div>

          <div className="w-4 shrink-0 @bp-sm:w-20" aria-hidden />
        </div>
      )}
    </header>
  );
};

export default AuthHeader;
